import React, { useState, useRef, useCallback } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  Chip,
} from "@mui/material";
import RswEditor, { EditorProvider } from "react-simple-wysiwyg";
import DataObjectIcon from "@mui/icons-material/DataObject";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import AddIcon from "@mui/icons-material/Add";
import DrawIcon from "@mui/icons-material/Draw";
import ImageIcon from "@mui/icons-material/Image";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import CloseIcon from "@mui/icons-material/Close";
import EventIcon from "@mui/icons-material/Event";
import axios from "axios";
import EmailTemplatesDialog, { EmailTemplate } from "./EmailTemplatesDialog";
import LoadingButton from "./LoadingButton";
import ImagePicker from "./ImagePicker";
import AISnippetDialog from "./AISnippetDialog";
import SpamCheckerDialog from "./SpamCheckerDialog";
import { hostname } from "../utils";
import { MEETING_TRACKING_LINK_VARIABLE } from "../constants";
import PsychologyIcon from "@mui/icons-material/Psychology";
import SecurityIcon from "@mui/icons-material/Security";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WebIcon from "@mui/icons-material/Web";
import HeyGenVideoDialog, { HeyGenVideoConfig } from "./HeyGenVideoDialog";
import LandingPageDialog, { LandingPageConfig } from "./LandingPageDialog";
import heygenLogo from "../img/heygen-square.png";

const STANDARD_MEMBER_FIELDS = [
  { key: "firstName", label: "First Name", icon: <PersonIcon fontSize="small" /> },
  { key: "lastName", label: "Last Name", icon: <PersonIcon fontSize="small" /> },
  { key: "email", label: "Email", icon: <EmailIcon fontSize="small" /> },
  { key: "phone", label: "Phone", icon: <PhoneIcon fontSize="small" /> },
  { key: "linkedinUrl", label: "LinkedIn URL", icon: <LinkedInIcon fontSize="small" /> },
];


const COMMON_EMOJIS = ["👋", "😊", "🎉", "👍", "🚀", "💡", "📧", "📞", "🤝", "⭐", "✅", "🔗"];

interface SchedulingIntegration {
  id: string;
  name?: string;
  instanceName?: string;
}

interface HeyGenIntegration {
  id: string;
  instanceName?: string;
  type: string;
}

interface EmailTemplateEditorProps {
  value: string;
  subject?: string;
  onChange: (data: { subject?: string; content?: string }) => void;
  height?: number;
  disabled?: boolean;
  enableVariables?: boolean;
  enableTemplates?: boolean;
  enableInsertMenu?: boolean;
  enableAIGenerate?: boolean;
  enableSpintax?: boolean;
  enableAISnippet?: boolean;
  enableSpamChecker?: boolean;
  enableMeetingTrackingLink?: boolean;
  enableHeyGenVideo?: boolean;
  enableLandingPage?: boolean;
  schedulingIntegrations?: SchedulingIntegration[];
  heygenIntegrations?: HeyGenIntegration[];
  heygenVideoConfig?: HeyGenVideoConfig | null;
  landingPageConfig?: LandingPageConfig | null;
  // Second param is the new content with placeholder already inserted
  onHeyGenVideoConfigChange?: (config: HeyGenVideoConfig | null, newContent?: string) => void;
  onLandingPageConfigChange?: (config: LandingPageConfig | null, newContent?: string) => void;
  onMeetingLinkInserted?: (integrationId: string, newContent: string) => void;
  customVariableFields?: { key: string; label: string }[];
  getToken?: () => Promise<string>;
  companyId?: string;
  sequenceId?: string;
  onAISuccess?: (message: string) => void;
  onAIError?: (error: string) => void;
}

const EmailTemplateEditor: React.FC<EmailTemplateEditorProps> = ({
  value,
  subject,
  onChange,
  height = 300,
  disabled = false,
  enableVariables = true,
  enableTemplates = true,
  enableInsertMenu = true,
  enableAIGenerate = false,
  enableSpintax = false,
  enableAISnippet = false,
  enableSpamChecker = false,
  enableMeetingTrackingLink = false,
  enableHeyGenVideo = false,
  enableLandingPage = false,
  schedulingIntegrations = [],
  heygenIntegrations = [],
  heygenVideoConfig,
  landingPageConfig,
  onHeyGenVideoConfigChange,
  onLandingPageConfigChange,
  onMeetingLinkInserted,
  customVariableFields = [],
  getToken,
  companyId,
  sequenceId,
  onAISuccess,
  onAIError,
}) => {
  const [variablesAnchorEl, setVariablesAnchorEl] = useState<null | HTMLElement>(null);
  const [insertMenuAnchorEl, setInsertMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState(false);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [imageSizeDialogOpen, setImageSizeDialogOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState("");
  const [imageWidth, setImageWidth] = useState("400");
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [contentInstructions, setContentInstructions] = useState("");
  const [generatingContent, setGeneratingContent] = useState(false);
  const [generatingSpintax, setGeneratingSpintax] = useState(false);
  const [meetingLinkDialogOpen, setMeetingLinkDialogOpen] = useState(false);
  const [meetingLinkText, setMeetingLinkText] = useState("Book a meeting");
  const [selectedSchedulingIntegrationId, setSelectedSchedulingIntegrationId] = useState("");
  const [aiSnippetDialogOpen, setAISnippetDialogOpen] = useState(false);
  const [spamCheckerDialogOpen, setSpamCheckerDialogOpen] = useState(false);
  const [heygenVideoDialogOpen, setHeygenVideoDialogOpen] = useState(false);
  const [landingPageDialogOpen, setLandingPageDialogOpen] = useState(false);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const savedSelectionRef = useRef<Range | null>(null);

  const uniqueCustomFields = customVariableFields.filter(
    (field) => !STANDARD_MEMBER_FIELDS.map((f) => f.key).includes(field.key)
  );

  // Save the current selection/cursor position before opening menus
  const saveSelection = useCallback(() => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      // Check if the selection is within our editor
      const editorElement = editorContainerRef.current?.querySelector('[contenteditable="true"]');
      if (editorElement && editorElement.contains(range.commonAncestorContainer)) {
        savedSelectionRef.current = range.cloneRange();
      }
    }
  }, []);

  // Insert HTML content at the saved cursor position, or append to end if no selection
  const insertAtCursor = useCallback((htmlContent: string) => {
    const editorElement = editorContainerRef.current?.querySelector('[contenteditable="true"]') as HTMLElement;
    if (!editorElement) {
      // Fallback: append to end
      onChange({ content: value + htmlContent });
      return;
    }

    // Focus the editor
    editorElement.focus();

    // Try to restore the saved selection
    if (savedSelectionRef.current) {
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(savedSelectionRef.current);
      }
    }

    // Use execCommand to insert HTML at cursor position
    // This works with contenteditable elements and maintains undo history
    document.execCommand('insertHTML', false, htmlContent);

    // Clear saved selection
    savedSelectionRef.current = null;

    // Trigger onChange with the new content
    // Small delay to let the DOM update
    setTimeout(() => {
      if (editorElement) {
        onChange({ content: editorElement.innerHTML });
      }
    }, 0);
  }, [value, onChange]);

  const handleInsertVariable = (key: string) => {
    insertAtCursor(`{{${key}}}`);
    setVariablesAnchorEl(null);
  };

  const handleInsertSignature = () => {
    setInsertMenuAnchorEl(null);
    insertAtCursor("{{accountSignature}}");
    onAISuccess?.("Signature placeholder inserted");
  };

  const handleImageSelect = (imageUrl: string) => {
    if (imageUrl) {
      setSelectedImageUrl(imageUrl);
      setImageWidth("400");
      setImageSizeDialogOpen(true);
    }
  };

  const handleInsertImageWithSize = () => {
    if (selectedImageUrl) {
      const width = parseInt(imageWidth, 10);
      const style = width > 0 ? `max-width: ${width}px; width: 100%;` : "max-width: 100%;";
      insertAtCursor(`<img src="${selectedImageUrl}" alt="" style="${style}">`);
      onAISuccess?.("Image inserted");
    }
    setImageSizeDialogOpen(false);
    setSelectedImageUrl("");
  };

  const handleInsertEmoji = (emoji: string) => {
    insertAtCursor(emoji);
    setInsertMenuAnchorEl(null);
  };

  const handleOpenMeetingLinkDialog = () => {
    setInsertMenuAnchorEl(null);
    setMeetingLinkText("Book a meeting");
    setSelectedSchedulingIntegrationId(schedulingIntegrations.length === 1 ? schedulingIntegrations[0].id : "");
    setMeetingLinkDialogOpen(true);
  };

  const handleInsertMeetingTrackingLink = () => {
    if (!selectedSchedulingIntegrationId) return;

    // Insert the meeting tracking link as a placeholder with hyperlink
    // The actual link URL will be replaced at send time
    const linkHtml = `<a href="{{${MEETING_TRACKING_LINK_VARIABLE}}}">${meetingLinkText}</a>`;

    // Insert at cursor position
    const editorElement = editorContainerRef.current?.querySelector('[contenteditable="true"]') as HTMLElement;
    if (editorElement) {
      editorElement.focus();
      if (savedSelectionRef.current) {
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(savedSelectionRef.current);
        }
      }
      document.execCommand('insertHTML', false, linkHtml);
      savedSelectionRef.current = null;

      // Get updated content and notify parent
      setTimeout(() => {
        const newContent = editorElement.innerHTML;
        onMeetingLinkInserted?.(selectedSchedulingIntegrationId, newContent);
      }, 0);
    } else {
      // Fallback: append to end
      const newContent = value + linkHtml;
      onMeetingLinkInserted?.(selectedSchedulingIntegrationId, newContent);
    }

    setMeetingLinkDialogOpen(false);
    onAISuccess?.("Meeting tracking link inserted");
  };

  const handleSelectTemplate = (template: EmailTemplate) => {
    // Convert plain text to HTML: replace newlines with <br>
    const htmlBody = template.body
      .replace(/\n\n/g, "<br><br>")  // Double newlines
      .replace(/\n/g, "<br>");        // Single newlines
    onChange({ subject: template.subject, content: htmlBody });
    onAISuccess?.(`Template "${template.name}" applied`);
  };

  const handleGenerateContent = async () => {
    if (!getToken || !companyId) {
      onAIError?.("Missing required configuration for AI generation");
      return;
    }
    setGeneratingContent(true);
    setGenerateDialogOpen(false);
    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/content/generate`,
        {
          companyId,
          instructions: contentInstructions,
          contentType: "email",
          subject: subject || "",
          existingContent: value || "",
          context: sequenceId ? { sequenceId } : undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onChange({ subject: response.data.subject, content: response.data.content });
      onAISuccess?.("Content generated successfully");
    } catch (err: any) {
      onAIError?.(err.response?.data?.error || "Failed to generate content");
    } finally {
      setGeneratingContent(false);
    }
  };

  const handleGenerateSpintax = async () => {
    if (!getToken || !companyId) {
      onAIError?.("Missing required configuration for spintax generation");
      return;
    }
    if (!value && !subject) {
      onAIError?.("Please add some content or subject first before generating spintax");
      return;
    }
    setGeneratingSpintax(true);
    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/content/spintax`,
        { companyId, content: value, subject: subject || "", aggressive: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updates: { content?: string; subject?: string } = {};
      if (response.data.content) {
        updates.content = response.data.content;
      }
      if (response.data.subject) {
        updates.subject = response.data.subject;
      }
      if (Object.keys(updates).length > 0) {
        onChange(updates);
      }
      onAISuccess?.(response.data.explanation || "Spintax generated successfully");
    } catch (err: any) {
      onAIError?.(err.response?.data?.error || "Failed to generate spintax");
    } finally {
      setGeneratingSpintax(false);
    }
  };

  const handleInsertAISnippet = (snippetPlaceholder: string) => {
    insertAtCursor(snippetPlaceholder);
    onAISuccess?.("AI snippet placeholder inserted");
  };

  // Check if content already has a HeyGen video placeholder
  const hasHeyGenPlaceholder = value?.includes("{{HeyGenVideo") || false;

  // Check if content already has a Landing Page placeholder
  const hasLandingPagePlaceholder = value?.includes("{{LandingPage") || false;

  const handleSaveHeyGenVideoConfig = (config: HeyGenVideoConfig, placeholder: string) => {
    // If placeholder already exists, just update the config (don't insert new placeholder)
    if (hasHeyGenPlaceholder) {
      // Replace existing placeholder with new one (updated variables)
      const heygenPattern = /\{\{HeyGenVideo(?::\s*[^}]*)?\}\}/g;
      const newContent = value.replace(heygenPattern, placeholder);
      onHeyGenVideoConfigChange?.(config, newContent);
      onAISuccess?.(`HeyGen video configuration updated.`);
      return;
    }

    // Insert placeholder at cursor position and get the new content
    const editorElement = editorContainerRef.current?.querySelector('[contenteditable="true"]') as HTMLElement;

    let newContent = value + placeholder; // Default: append to end

    if (editorElement) {
      // Focus the editor
      editorElement.focus();

      // Try to restore the saved selection
      if (savedSelectionRef.current) {
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(savedSelectionRef.current);
        }
      }

      // Use execCommand to insert HTML at cursor position
      document.execCommand('insertHTML', false, placeholder);

      // Clear saved selection
      savedSelectionRef.current = null;

      // Get the updated content
      newContent = editorElement.innerHTML;
    }

    // Save the config AND updated content together to avoid race condition
    onHeyGenVideoConfigChange?.(config, newContent);
    onAISuccess?.(`HeyGen video placeholder inserted. Video will be generated for each contact.`);
  };

  const handleSaveLandingPageConfig = (config: LandingPageConfig, placeholder: string) => {
    // If placeholder already exists, just update the config (don't insert new placeholder)
    if (hasLandingPagePlaceholder) {
      // Replace existing placeholder with new one
      const landingPagePattern = /\{\{LandingPage(?::\s*"[^"]*")?\}\}/g;
      const newContent = value.replace(landingPagePattern, placeholder);
      onLandingPageConfigChange?.(config, newContent);
      onAISuccess?.(`Landing page configuration updated.`);
      return;
    }

    // Insert placeholder at cursor position and get the new content
    const editorElement = editorContainerRef.current?.querySelector('[contenteditable="true"]') as HTMLElement;

    let newContent = value + placeholder; // Default: append to end

    if (editorElement) {
      // Focus the editor
      editorElement.focus();

      // Try to restore the saved selection
      if (savedSelectionRef.current) {
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(savedSelectionRef.current);
        }
      }

      // Use execCommand to insert HTML at cursor position
      document.execCommand('insertHTML', false, placeholder);

      // Clear saved selection
      savedSelectionRef.current = null;

      // Get the updated content
      newContent = editorElement.innerHTML;
    }

    // Save the config AND updated content together to avoid race condition
    onLandingPageConfigChange?.(config, newContent);
    onAISuccess?.(`Landing page placeholder inserted. A personalized page will be generated for each contact.`);
  };

  const showToolbar =
    enableVariables || enableTemplates || enableInsertMenu || enableAIGenerate || enableSpintax || enableAISnippet || enableSpamChecker || enableHeyGenVideo || enableLandingPage;

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  // Convert plain text to HTML if the value doesn't contain HTML tags
  const htmlValue = value && !value.includes("<")
    ? value.replace(/\n/g, "<br>")
    : value;

  return (
    <Box
      sx={{
        width: "100%",
        // Fix dark mode text color for HTML source view
        "& textarea": {
          color: isDarkMode ? "#fff" : "#000",
          backgroundColor: isDarkMode ? "#1e1e1e" : "#fff",
        },
      }}
    >
      <Box ref={editorContainerRef}>
        <EditorProvider>
          <RswEditor
            style={{ height }}
            containerProps={{
              style: {
                resize: "vertical",
                borderBottomLeftRadius: showToolbar ? 0 : 4,
                borderBottomRightRadius: showToolbar ? 0 : 4,
              },
            }}
            value={htmlValue}
            onChange={(e) => onChange({ content: e.target.value })}
            disabled={disabled}
          />
        </EditorProvider>
      </Box>

      {showToolbar && (
        <Box
          sx={(theme) => ({
            display: "flex",
            gap: 0.5,
            alignItems: "center",
            px: 1,
            py: 0.75,
            border: 1,
            borderColor:
              theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
            borderTop: "none",
            borderBottomLeftRadius: 4,
            borderBottomRightRadius: 4,
            backgroundColor: "action.hover",
          })}
        >
          {enableVariables && (
            <>
              <Tooltip title="Insert personalization variable">
                <IconButton
                  size="small"
                  onClick={(e) => { saveSelection(); setVariablesAnchorEl(e.currentTarget); }}
                  disabled={disabled}
                  sx={{ color: "text.secondary" }}
                >
                  <DataObjectIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={variablesAnchorEl}
                open={Boolean(variablesAnchorEl)}
                onClose={() => setVariablesAnchorEl(null)}
                PaperProps={{ sx: { maxHeight: 400, width: 280 } }}
              >
                <Typography variant="caption" sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}>
                  Member Fields
                </Typography>
                {STANDARD_MEMBER_FIELDS.map((field) => (
                  <MenuItem key={field.key} onClick={() => handleInsertVariable(field.key)} dense>
                    <ListItemIcon>{field.icon}</ListItemIcon>
                    <ListItemText
                      primary={field.label}
                      secondary={`{{${field.key}}}`}
                      secondaryTypographyProps={{ variant: "caption", fontFamily: "monospace" }}
                    />
                  </MenuItem>
                ))}
                {uniqueCustomFields.length > 0 && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}>
                      Custom Fields (non-null)
                    </Typography>
                    {uniqueCustomFields.map((field) => (
                      <MenuItem key={field.key} onClick={() => handleInsertVariable(field.key)} dense>
                        <ListItemIcon><DataObjectIcon fontSize="small" /></ListItemIcon>
                        <ListItemText
                          primary={field.label}
                          secondary={`{{${field.key}}}`}
                          secondaryTypographyProps={{ variant: "caption", fontFamily: "monospace" }}
                        />
                      </MenuItem>
                    ))}
                  </>
                )}
              </Menu>
            </>
          )}

          {enableSpintax && (
            <Tooltip title="Convert to spintax for variation">
              <span>
                <IconButton
                  size="small"
                  onClick={handleGenerateSpintax}
                  disabled={disabled || generatingSpintax || (!value && !subject)}
                  sx={{ color: "text.secondary" }}
                >
                  {generatingSpintax ? <CircularProgress size={18} /> : <ShuffleIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}

          {enableAIGenerate && (
            <Tooltip title="AI Generate content">
              <span>
                <IconButton
                  size="small"
                  onClick={() => { setContentInstructions(""); setGenerateDialogOpen(true); }}
                  disabled={disabled || generatingContent}
                  sx={{ color: "text.secondary" }}
                >
                  {generatingContent ? <CircularProgress size={18} /> : <AutoAwesomeIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}

          {enableAISnippet && (
            <Tooltip title="Insert AI Snippet (personalized text generated at send time)">
              <IconButton
                size="small"
                onClick={() => { saveSelection(); setAISnippetDialogOpen(true); }}
                disabled={disabled}
                sx={{ color: "text.secondary" }}
              >
                <PsychologyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {enableTemplates && (
            <Tooltip title="Insert email template">
              <IconButton
                size="small"
                onClick={() => setTemplatesDialogOpen(true)}
                disabled={disabled}
                sx={{ color: "text.secondary" }}
              >
                <LibraryBooksIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {enableSpamChecker && getToken && companyId && (
            <Tooltip title="Check for spam triggers">
              <IconButton
                size="small"
                onClick={() => setSpamCheckerDialogOpen(true)}
                disabled={disabled || !value}
                sx={{ color: "text.secondary" }}
              >
                <SecurityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {enableInsertMenu && (
            <>
              <Tooltip title="Insert signature, image, or emoji">
                <IconButton
                  size="small"
                  onClick={(e) => { saveSelection(); setInsertMenuAnchorEl(e.currentTarget); }}
                  disabled={disabled}
                  sx={{ color: "text.secondary" }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={insertMenuAnchorEl}
                open={Boolean(insertMenuAnchorEl)}
                onClose={() => setInsertMenuAnchorEl(null)}
                PaperProps={{ sx: { minWidth: 200 } }}
              >
                <MenuItem onClick={handleInsertSignature}>
                  <ListItemIcon><DrawIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Insert Signature" />
                </MenuItem>
                <MenuItem
                  onClick={() => { setInsertMenuAnchorEl(null); setImagePickerOpen(true); }}
                  disabled={!getToken || !companyId}
                >
                  <ListItemIcon><ImageIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Insert Image" />
                </MenuItem>
                {enableMeetingTrackingLink && schedulingIntegrations.length > 0 && (
                  <MenuItem onClick={handleOpenMeetingLinkDialog}>
                    <ListItemIcon><EventIcon fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Insert Meeting Link"
                      secondary="Tracked calendar booking link"
                      secondaryTypographyProps={{ variant: "caption" }}
                    />
                  </MenuItem>
                )}
                {enableHeyGenVideo && heygenIntegrations.length > 0 && (
                  <MenuItem
                    onClick={() => { setInsertMenuAnchorEl(null); setHeygenVideoDialogOpen(true); }}
                  >
                    <ListItemIcon>
                      {hasHeyGenPlaceholder || heygenVideoConfig ? (
                        <CheckCircleIcon fontSize="small" color="primary" />
                      ) : (
                        <img src={heygenLogo} alt="HeyGen" style={{ width: 20, height: 20 }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {hasHeyGenPlaceholder ? "Edit HeyGen Video" : "Add HeyGen Video"}
                          <Chip label="Beta" size="small" sx={{ height: 18, fontSize: "0.65rem" }} />
                        </Box>
                      }
                      secondary={
                        heygenVideoConfig
                          ? `Template: ${heygenVideoConfig.templateName}`
                          : "Personalized video per contact"
                      }
                      secondaryTypographyProps={{ variant: "caption" }}
                    />
                  </MenuItem>
                )}
                {enableLandingPage && getToken && companyId && (
                  <MenuItem
                    onClick={() => { setInsertMenuAnchorEl(null); setLandingPageDialogOpen(true); }}
                  >
                    <ListItemIcon>
                      {hasLandingPagePlaceholder || landingPageConfig ? (
                        <CheckCircleIcon fontSize="small" color="primary" />
                      ) : (
                        <WebIcon fontSize="small" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {hasLandingPagePlaceholder ? "Edit Landing Page" : "Add Landing Page"}
                          <Chip label="Beta" size="small" sx={{ height: 18, fontSize: "0.65rem" }} />
                        </Box>
                      }
                      secondary={
                        landingPageConfig
                          ? "Landing page configured"
                          : "Personalized landing page per contact"
                      }
                      secondaryTypographyProps={{ variant: "caption" }}
                    />
                  </MenuItem>
                )}
                <Divider />
                <Typography variant="caption" sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}>
                  Quick Emojis
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", px: 1, pb: 1, gap: 0.5 }}>
                  {COMMON_EMOJIS.map((emoji) => (
                    <IconButton key={emoji} size="small" onClick={() => handleInsertEmoji(emoji)} sx={{ fontSize: "18px" }}>
                      {emoji}
                    </IconButton>
                  ))}
                </Box>
              </Menu>
            </>
          )}
        </Box>
      )}

      {enableTemplates && (
        <EmailTemplatesDialog
          open={templatesDialogOpen}
          onClose={() => setTemplatesDialogOpen(false)}
          onSelect={handleSelectTemplate}
        />
      )}

      {enableInsertMenu && getToken && companyId && (
        <ImagePicker
          open={imagePickerOpen}
          onClose={() => setImagePickerOpen(false)}
          onSelect={handleImageSelect}
          getToken={getToken}
          companyId={companyId}
        />
      )}

      <Dialog open={imageSizeDialogOpen} onClose={() => setImageSizeDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6">Image Size</Typography>
            <IconButton onClick={() => setImageSizeDialogOpen(false)} size="small"><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedImageUrl && (
            <Box sx={{ mb: 2, textAlign: "center" }}>
              <img
                src={selectedImageUrl}
                alt="Preview"
                style={{ maxWidth: "100%", maxHeight: 150, objectFit: "contain", borderRadius: 4 }}
              />
            </Box>
          )}
          <FormControl fullWidth>
            <InputLabel>Width</InputLabel>
            <Select
              value={imageWidth}
              label="Width"
              onChange={(e) => setImageWidth(e.target.value)}
            >
              <MenuItem value="200">Small (200px)</MenuItem>
              <MenuItem value="400">Medium (400px)</MenuItem>
              <MenuItem value="600">Large (600px)</MenuItem>
              <MenuItem value="0">Full Width</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageSizeDialogOpen(false)} color="inherit">Cancel</Button>
          <Button variant="contained" onClick={handleInsertImageWithSize}>Insert</Button>
        </DialogActions>
      </Dialog>

      {enableAIGenerate && (
        <Dialog open={generateDialogOpen} onClose={() => setGenerateDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography variant="h6">AI Generate Email Content</Typography>
              <IconButton onClick={() => setGenerateDialogOpen(false)} size="small"><CloseIcon /></IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Describe what you want the AI to write.
            </Typography>
            <TextField
              autoFocus
              label="What should this message say?"
              placeholder="e.g., Write a follow-up email asking about their interest in scheduling a demo..."
              multiline
              rows={4}
              fullWidth
              value={contentInstructions}
              onChange={(e) => setContentInstructions(e.target.value)}
              helperText="Be specific about the purpose, tone, and call-to-action"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGenerateDialogOpen(false)} color="inherit">Cancel</Button>
            <LoadingButton variant="contained" onClick={handleGenerateContent} loading={generatingContent} disabled={!contentInstructions.trim()}>
              Generate
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}

      {/* Meeting Link Dialog */}
      <Dialog open={meetingLinkDialogOpen} onClose={() => setMeetingLinkDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6">Insert Meeting Link</Typography>
            <IconButton onClick={() => setMeetingLinkDialogOpen(false)} size="small"><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Insert a tracked calendar booking link. Views and bookings will be tracked for each recipient.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Scheduling Integration</InputLabel>
            <Select
              value={selectedSchedulingIntegrationId}
              onChange={(e) => setSelectedSchedulingIntegrationId(e.target.value)}
              label="Scheduling Integration"
            >
              {schedulingIntegrations.map((integration) => (
                <MenuItem key={integration.id} value={integration.id}>
                  {integration.instanceName || integration.name || "Scheduling"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Link Text"
            value={meetingLinkText}
            onChange={(e) => setMeetingLinkText(e.target.value)}
            placeholder="e.g., Book a meeting, Schedule a call"
            helperText="The clickable text that will appear in the email"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMeetingLinkDialogOpen(false)} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            onClick={handleInsertMeetingTrackingLink}
            disabled={!selectedSchedulingIntegrationId || !meetingLinkText.trim()}
          >
            Insert
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Snippet Dialog */}
      {enableAISnippet && (
        <AISnippetDialog
          open={aiSnippetDialogOpen}
          onClose={() => setAISnippetDialogOpen(false)}
          onInsert={handleInsertAISnippet}
          customVariableFields={customVariableFields}
        />
      )}

      {/* Spam Checker Dialog */}
      {enableSpamChecker && getToken && companyId && (
        <SpamCheckerDialog
          open={spamCheckerDialogOpen}
          onClose={() => setSpamCheckerDialogOpen(false)}
          subject={subject || ""}
          content={value}
          getToken={getToken}
          companyId={companyId}
          onApplyRecommendations={({ subject: newSubject, content: newContent }) => {
            onChange({
              ...(newSubject !== undefined && { subject: newSubject }),
              ...(newContent !== undefined && { content: newContent }),
            });
            onAISuccess?.("Spam-optimized content applied");
          }}
        />
      )}

      {/* HeyGen Video Dialog */}
      {enableHeyGenVideo && getToken && companyId && (
        <HeyGenVideoDialog
          open={heygenVideoDialogOpen}
          onClose={() => setHeygenVideoDialogOpen(false)}
          onSave={handleSaveHeyGenVideoConfig}
          getToken={getToken}
          companyId={companyId}
          heygenIntegrations={heygenIntegrations}
          existingConfig={heygenVideoConfig}
          customVariableFields={customVariableFields}
        />
      )}

      {/* Landing Page Dialog */}
      {enableLandingPage && getToken && companyId && (
        <LandingPageDialog
          open={landingPageDialogOpen}
          onClose={() => setLandingPageDialogOpen(false)}
          onSave={handleSaveLandingPageConfig}
          getToken={getToken}
          companyId={companyId}
          existingConfig={landingPageConfig}
          customVariableFields={customVariableFields}
        />
      )}
    </Box>
  );
};

export default EmailTemplateEditor;
